import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal, Steps, Button, Spin, Checkbox, Form, Input, message,
  Select, Badge, Avatar, Tabs, DatePicker, Popconfirm, Image,
} from "antd";
import {
  PlayCircleOutlined, FileTextOutlined, CheckOutlined,
  ClockCircleOutlined, UserOutlined, PlusOutlined,
} from "@ant-design/icons";
import { FiFileText, FiSave, FiEdit2, FiTrash2, FiPrinter } from "react-icons/fi";
import { BiDollarCircle, BiPlus } from "react-icons/bi";
import { MdHistory } from "react-icons/md";
import { TbClipboardList } from "react-icons/tb";
import { IoClose, IoTrash } from "react-icons/io5";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";

import drEasyApi from "../../../Api/Customapi";
import { getImg } from "../../../Api/getImg";
import { getUserReport } from "../../../Api/getUserReport";
import { calculateAge, formatDateTime } from "../../../utils/helpers";
import ImageUploader from "../CurrentActivePatient/ImageUploader";
import { buildPrintLymphedemaHTML } from "../CurrentActivePatient/Tabs/PrintAssemble";
import { buildPrintPhisoHTML } from "../CurrentActivePatient/Tabs/PrintPhisoAssessment";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";

// ─── Constants ────────────────────────────────────────────────────────────────

const REPORT_TYPE_INT    = { General: 0, PhysioTherapy: 1, Lymphedema: 2 };
const REPORT_TYPE_STRING = { 0: "General", 1: "PhysioTherapy", 2: "Lymphedema" };
const ROW_COUNT          = 9;
const ROW_INDICES        = Array.from({ length: ROW_COUNT }, (_, i) => i);

const TAB_VISIBILITY = {
  General:       ["notes", "sessions"],
  PhysioTherapy: ["assessment", "sessions"],
  Lymphedema:    ["lymphedema", "sessions"],
};

const ALL_TABS = [
  { id: "notes",      label: "General Notes",          icon: FiFileText       },
  { id: "sessions",   label: "Sessions",               icon: MdHistory        },
  { id: "assessment", label: "PhysioTherapy",          icon: TbClipboardList  },
  { id: "lymphedema", label: "Lymphedema Assessment",  icon: TbClipboardList  },
];

const REPORT_TYPE_OPTIONS = [
  { value: 0, label: "General",       description: "Standard patient notes and general documentation"             },
  { value: 1, label: "PhysioTherapy", description: "Physiotherapy assessment, treatment plans and progress notes" },
  { value: 2, label: "Lymphedema",    description: "Lymphedema-specific assessment and treatment tracking"        },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toReportTypeString = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return REPORT_TYPE_STRING[v] ?? null;
  if (typeof v === "string" && v in REPORT_TYPE_INT) return v;
  return null;
};

const toReportTypeInt = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return v;
  return REPORT_TYPE_INT[v] ?? 0;
};

const emptyAssessmentApiState = () => ({
  diagnosis: "", pastHistory: "", chiefComplain: "", jobAndOccupation: "",
  painScore: 0, painDistribution: "", lymphaticEdema: false,
  radioInvestigations: "", labNotes: false, labResults: "",
  precautions: "", ptAssessment: "", ptPlanOfTreatment: "",
  sessionsNum: 0, progressNotes: "", tttGoals: "", discharge: "",
  otherNotes: "", operations: "", presentHistory: "", skinAbnormalities: "",
  treatmentMLD: false, treatmentPCD: false, treatmentTECAR: false,
  treatmentBandaging: false, treatmentExercises: false, treatmentOther: "",
});

export const buildAssessmentApiState = (report) => ({
  diagnosis:          report.diagnosis          || "",
  pastHistory:        report.pastHistory        || "",
  chiefComplain:      report.chiefComplain      || "",
  jobAndOccupation:   report.jobAndOccupation   || "",
  painScore:          report.painScore          ?? 0,
  painDistribution:   report.painDistribution   || "",
  lymphaticEdema:     report.lymphaticEdema     ?? false,
  radioInvestigations:report.radioInvestigations|| "",
  labNotes:           report.labNotes           ?? false,
  labResults:         report.labResults         || "",
  precautions:        report.precautions        || "",
  ptAssessment:       report.ptAssessment       || "",
  ptPlanOfTreatment:  report.ptPlanOfTreatment  || "",
  sessionsNum:        report.sessionsNum        ?? 0,
  progressNotes:      report.progressNotes      || "",
  tttGoals:           report.tttGoals           || "",
  discharge:          report.discharge          || "",
  otherNotes:         report.otherNotes         || "",
  operations:         report.operations         || "",
  presentHistory:     report.presentHistory     || "",
  skinAbnormalities:  report.skinAbnormalities  || "",
  treatmentMLD:       report.treatmentMLD       ?? false,
  treatmentPCD:       report.treatmentPCD       ?? false,
  treatmentTECAR:     report.treatmentTECAR     ?? false,
  treatmentBandaging: report.treatmentBandaging ?? false,
  treatmentExercises: report.treatmentExercises ?? false,
  treatmentOther:     report.treatmentOther     || "",
});

const hydrateFromReport = (report, { setFreeFormNotes, setSavedSections, setAssessmentApiState }) => {
  setFreeFormNotes(report.body || "");
  setSavedSections(
    (report.reportSections || []).map((s) => ({
      sectionId: s.id,
      title:     s.title   || "",
      content:   s.body    || "",
      photoIDs:  s.photoIds|| [],
      createdAt: s.createdAt,
    }))
  );
  setAssessmentApiState(buildAssessmentApiState(report));
};

// ─── Measurement API helpers ──────────────────────────────────────────────────
export function PaginationControl({ current, total, onChange }) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-3 bg-primary/5 border border-primary/20 rounded-lg">
      <button onClick={() => onChange(current - 1)} disabled={current === 0}
        className="px-2 py-0.5 flex items-center gap-1 rounded border border-primary text-primary font-semibold text-xs hover:bg-primary hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
        <BsArrowLeft/> <span className="hidden md:flex"> Prev</span>
      </button>
      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
        Report {current + 1} of {total}
      </span>
      <button onClick={() => onChange(current + 1)} disabled={current === total - 1}
        className="px-2 py-0.5 flex items-center gap-1 rounded border border-primary text-primary font-semibold text-xs hover:bg-primary hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
       <span className="hidden md:flex"> Next  </span> <BsArrowRight/>
      </button>
    </div>
  );
}

export function SignatureFooter() {
  return (
    <div className="flex justify-between pt-6 mt-4 border-t border-gray-300">
      {["P.T Specialist Signature", "Consultant Signature"].map((label) => (
        <div key={label} className="text-center">
          <div className="w-36 border-b border-gray-500 h-8 mb-1" />
          <span className="text-sm font-semibold">{label}</span>
        </div>
      ))}
    </div>
  );
}

export function AssessmentFormFields() {
  return (
    <>
      {/* Name / Age / Gender / Date */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
        <Form.Item name="ptName" label={<span className="font-semibold text-sm">Name:</span>} className="flex-1 min-w-[140px] mb-0">
          <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent disabled:bg-white disabled:text-black" />
        </Form.Item>
        <Form.Item name="ptAge" label={<span className="font-semibold text-sm">Age:</span>} className="w-12 mb-0">
          <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent disabled:bg-white disabled:text-black" />
        </Form.Item>
        <Form.Item name="ptGender" label={<span className="font-semibold text-sm">Gender:</span>} className="mb-0 disabled:bg-white disabled:text-black">
          <Checkbox.Group disabled className="disabled:bg-white disabled:text-black">
            <Checkbox className="disabled:bg-white disabled:text-black" value="M">M.</Checkbox>
            <Checkbox className="disabled:bg-white disabled:text-black" value="F">F.</Checkbox>
          </Checkbox.Group>
        </Form.Item>
        <Form.Item   name="ptDate" label={<span className="font-semibold text-sm">Date:</span>} className="mb-0">
          <Input disabled className="border-0 border-b border-gray-500 rounded-none w-24 px-0 bg-transparent disabled:bg-white disabled:text-black" />
        </Form.Item>
      </div>

      {/* Diagnosis / Phone */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
        <Form.Item name="ptDiagnosis" label={<span className="font-semibold text-sm">Diagnosis:</span>} className="flex-1 min-w-[140px] mb-0">
          <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
        </Form.Item>
        <Form.Item  name="ptPhone" label={<span className="font-semibold text-sm">Phone Num:</span>} className="flex-1 min-w-[130px] mb-0">
          <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent disabled:bg-white disabled:text-black" />
        </Form.Item>
      </div>

      {/* Past History */}
      <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
        <Form.Item name="ptPastHistory" label={<span className="font-semibold text-sm">Past. History:</span>} className="mb-0">
          <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
        </Form.Item>
      </div>

      {/* Chief Complain */}
      <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
        <Form.Item name="ptChiefComplain" label={<span className="font-semibold text-sm">Chief Complain:</span>} className="mb-0">
          <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
        </Form.Item>
      </div>

      {/* Job */}
      <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
        <Form.Item name="ptJob" label={<span className="font-semibold text-sm">Job & Occupation:</span>} className="mb-0">
          <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
        </Form.Item>
      </div>

      {/* Pain / Lymphedema */}
      <div className="flex flex-col md:flex-row flex-wrap gap-x-4 gap-y-3 md:items-end pb-3 mb-3 border-b border-dashed border-gray-300">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold whitespace-nowrap">Pain score:</span>
          <Form.Item name="ptPainScore" className="mb-0 w-10">
            <Input className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent" />
          </Form.Item>
          <span className="text-sm font-semibold">/10</span>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[110px]">
          <span className="text-sm font-semibold whitespace-nowrap">Pain distribution:</span>
          <Form.Item name="ptPainDistribution" className="mb-0 flex-1">
            <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
          </Form.Item>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold whitespace-nowrap">Lymphatic edema:</span>
          <Form.Item name="ptLymphaticEdema" className="mb-0">
            <Checkbox.Group>
              <Checkbox value="yes">Yes</Checkbox>
              <Checkbox value="no">No.</Checkbox>
            </Checkbox.Group>
          </Form.Item>
        </div>
      </div>

      {/* Radio Investigations */}
      <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
        <Form.Item name="ptRadioInvestigations" label={<span className="font-semibold text-sm">Radio Investigations:</span>} className="mb-0">
          <TextArea autoSize={{ minRows: 2 }} className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent" />
        </Form.Item>
      </div>

      {/* Lab */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Lab Notes:</span>
          <Form.Item name="ptLabNotes" className="mb-0">
            <Checkbox.Group>
              <Checkbox value="yes">Yes</Checkbox>
              <Checkbox value="no">No.</Checkbox>
            </Checkbox.Group>
          </Form.Item>
        </div>
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm font-semibold whitespace-nowrap">Results:</span>
          <Form.Item name="ptLabResults" className="mb-0 flex-1">
            <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
          </Form.Item>
        </div>
      </div>
    </>
  );
}

function BodyDiagramSVG({ label, isBack = false }) {
  return (
    <div className="text-center">
      <svg viewBox="0 0 80 170" width={70} height={148} className="mx-auto opacity-70">
        <ellipse cx="40" cy="11" rx="10" ry="11" fill="none" stroke="#444" strokeWidth="1.5"/>
        <rect x="36" y="21" width="8" height="7" fill="none" stroke="#444" strokeWidth="1.5"/>
        <path d="M24 28 Q21 48 21 70 L59 70 Q59 48 56 28 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
        <path d="M24 30 Q13 44 10 76 L16 76 Q18 48 27 36 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
        <path d="M56 30 Q67 44 70 76 L64 76 Q62 48 53 36 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
        <path d="M31 70 Q27 102 25 148 L35 148 Q35 102 37 70 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
        <path d="M49 70 Q53 102 55 148 L45 148 Q45 102 43 70 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
        <line x1="40" y1="28" x2="40" y2="70" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
        {isBack ? (
          <>
            <line x1="21" y1="38" x2="59" y2="38" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
            <line x1="21" y1="50" x2="59" y2="50" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
            <line x1="21" y1="62" x2="59" y2="62" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
          </>
        ) : (
          <>
            <line x1="21" y1="48" x2="59" y2="48" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
            <line x1="21" y1="59" x2="59" y2="59" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
          </>
        )}
      </svg>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  );
}
export function buildMeasurementPayload(pointOfMeasurement, idx, f) {
  const payload = { pointOfMeasurement };
  [1, 2, 3, 4].forEach((col) => {
    const raw = f[`session${col}Date`];
    payload[`session${col}Date`] =
      raw && dayjs.isDayjs(raw) && raw.isValid() ? raw.toISOString() : null;
    payload[`session${col}RT`] = Number(f[`ptMeas_${idx}_${col}_RT`]) || 0;
    payload[`session${col}LT`] = Number(f[`ptMeas_${idx}_${col}_LT`]) || 0;
  });
  return payload;
}

async function addMeasurement(reportId, pointOfMeasurement, idx, f) {
  const res = await drEasyApi.post("/api/Reports/AddLymphedemaMeasurement", {
    reportId, ...buildMeasurementPayload(pointOfMeasurement, idx, f),
  });
  return res.data;
}

async function updateMeasurement(measurementId, pointOfMeasurement, idx, f) {
  const res = await drEasyApi.put("/api/Reports/UpdateLymphedemaMeasurement", {
    measurementId, ...buildMeasurementPayload(pointOfMeasurement, idx, f),
  });
  return res.data;
}

async function deleteMeasurementApi(measurementId) {
  await drEasyApi.delete("/api/Reports/DeleteLymphedemaMeasurement", {
    params: { MeasurementId: measurementId },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: General Notes Tab
// ─────────────────────────────────────────────────────────────────────────────

export function GeneralNotesPanel({
  reportId, freeFormNotes, setFreeFormNotes,
  savedSections, setSavedSections,
  onSaveNotes, fetchReport,
  reportCount, currentReportIndex, onReportIndexChange,
}) {
  const [newSection,      setNewSection]      = useState(null);
  const [editingSectionId,setEditingSectionId]= useState(null);
  const [savingNotes,     setSavingNotes]     = useState(false);
  const [savingSection,   setSavingSection]   = useState(false);
  const [seeMore,         setSeeMore]         = useState(false);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try { await onSaveNotes(); await fetchReport?.(); message.success("Notes saved"); }
    catch { message.error("Failed to save notes"); }
    finally { setSavingNotes(false); }
  };

  const handleAddSection = async () => {
    if (!newSection.title.trim() || !newSection.content.trim()) {
      return message.error("Please fill in title and content");
    }
    setSavingSection(true);
    try {
      const res = await drEasyApi.post("/api/Reports/AddSection", {
        reportId, title: newSection.title, body: newSection.content, photoIDs: newSection.photoIDs,
      });
      setSavedSections((prev) => [{
        sectionId: res.data.id || res.data,
        title:     res.data.title   || newSection.title,
        content:   res.data.body    || newSection.content,
        photoIDs:  res.data.photoIds|| newSection.photoIDs,
        createdAt: res.data.createdAt || new Date().toISOString(),
      }, ...prev]);
      await fetchReport?.();
      message.success("Section added");
      setNewSection(null);
    } catch { message.error("Failed to add section"); }
    finally { setSavingSection(false); }
  };

  const handleEditSection = async (section) => {
    if (!section.title.trim() || !section.content.trim())
      return message.error("Please fill in title and content");
    setSavingSection(true);
    try {
      await drEasyApi.put("/api/Reports/EditSection", {
        sectionId: section.sectionId, title: section.title,
        body: section.content, photoIDs: section.photoIDs,
      });
      message.success("Section updated");
      setEditingSectionId(null);
    } catch { message.error("Failed to update section"); }
    finally { setSavingSection(false); }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      await drEasyApi.delete(`/api/Reports/DeleteSection/${sectionId}`);
      setSavedSections((prev) => prev.filter((s) => s.sectionId !== sectionId));
      message.success("Section deleted");
    } catch { message.error("Failed to delete section"); }
  };

  const updateEditSection = (sectionId, field, value) =>
    setSavedSections((prev) =>
      prev.map((s) => s.sectionId === sectionId ? { ...s, [field]: value } : s)
    );

 return (
    <div>
      {/* Header row — sticky */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm flex items-center justify-between gap-2 px-4 py-2 -mx-4">
        {reportCount > 1 ? (
          <PaginationControl
            current={currentReportIndex}
            total={reportCount}
            onChange={onReportIndexChange}
          />
        ) : <div />}
        <button
          onClick={handleSaveNotes}
          disabled={savingNotes}
          className="flex items-center gap-1.5 bg-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm font-semibold"
        >
          <FiEdit2 size={15} />
          <span className="hidden sm:block">{savingNotes ? "Saving..." : "Save Notes"}</span>
          <span className="sm:hidden">{savingNotes ? "..." : "Save"}</span>
        </button>
      </div>

      <div className="pt-4 space-y-4">
        {/* Free-form textarea */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">General Notes</label>
          <textarea
            value={freeFormNotes}
            onChange={(e) => setFreeFormNotes(e.target.value)}
            placeholder="Write your general observations and notes here..."
            className="w-full min-h-[140px] sm:min-h-[160px] p-3 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 resize-y text-sm"
          />
        </div>

        {/* Add section button */}
        <div>
          <button
            onClick={() => { setNewSection({ title: "", content: "", photoIDs: [] }); setEditingSectionId(null); }}
            disabled={newSection !== null}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/80 transition-colors disabled:opacity-50 text-sm font-semibold"
          >
            <BiPlus size={18} /> Add Section
          </button>
        </div>

        {/* New section form */}
        {newSection && (
          <div className="border-2 border-primary/40 rounded-lg p-3 sm:p-4 bg-primary/5">
            <h3 className="text-base font-semibold text-gray-800 mb-3">New Section</h3>
            <input
              type="text"
              value={newSection.title}
              onChange={(e) => setNewSection((p) => ({ ...p, title: e.target.value }))}
              placeholder="Section Title"
              className="w-full px-3 py-2 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 font-semibold mb-3 text-sm"
            />
            <textarea
              value={newSection.content}
              onChange={(e) => setNewSection((p) => ({ ...p, content: e.target.value }))}
              placeholder="Section content..."
              className="w-full min-h-[100px] p-3 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 resize-y mb-3 text-sm"
            />
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Images</label>
              <ImageUploader
                value={newSection.photoIDs}
                multiple
                onChange={(ids) => setNewSection((p) => ({ ...p, photoIDs: ids }))}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={handleAddSection}
                disabled={savingSection}
                className="flex items-center gap-2 bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary/80 disabled:opacity-50 text-sm"
              >
                <FiSave size={15} />
                {savingSection ? "Saving..." : "Save Section"}
              </button>
              <button
                onClick={() => setNewSection(null)}
                disabled={savingSection}
                className="flex items-center gap-2 bg-gray-500 text-white px-3 py-1.5 rounded-lg hover:bg-gray-600 text-sm"
              >
                <IoClose size={16} /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* Saved sections */}
        <div className="space-y-3">
          {savedSections.length > 0 && (
            <h3 className="text-base font-semibold text-gray-800">Report Sections</h3>
          )}
          {savedSections.map((section) => (
            <div key={section.sectionId} className="border-2 border-primary/20 rounded-lg p-3 sm:p-4 bg-white shadow-sm">
              {editingSectionId === section.sectionId ? (
                <>
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => updateEditSection(section.sectionId, "title", e.target.value)}
                    className="w-full px-3 py-2 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 font-semibold mb-3 text-sm"
                  />
                  <textarea
                    value={section.content}
                    onChange={(e) => updateEditSection(section.sectionId, "content", e.target.value)}
                    className="w-full min-h-[100px] p-3 border-2 border-primary/30 rounded-lg focus:outline-none resize-y mb-3 text-sm"
                  />
                  <div className="mb-3">
                    <ImageUploader
                      value={section.photoIDs}
                      multiple
                      onChange={(ids) => updateEditSection(section.sectionId, "photoIDs", ids)}
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handleEditSection(section)}
                      disabled={savingSection}
                      className="flex items-center gap-2 bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary/80 disabled:opacity-50 text-sm"
                    >
                      <FiSave size={15} />
                      {savingSection ? "Saving..." : "Save"}
                    </button>
                    <button
                      onClick={() => setEditingSectionId(null)}
                      disabled={savingSection}
                      className="flex items-center gap-2 bg-gray-500 text-white px-3 py-1.5 rounded-lg hover:bg-gray-600 text-sm"
                    >
                      <IoClose size={16} /> Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div>
                  {/* Section header */}
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-800 break-words">
                        {section.title}
                      </h4>
                      {section.createdAt && (
                        <p className="text-xs text-gray-500 mt-0.5">
                      Created: {formatDateTime(`${section.createdAt}Z`)}
                        </p>
                      )}
                      <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2 break-words">
                        {section.content}
                      </p>
                    </div>
                    {/* Action buttons — larger tap targets on mobile */}
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setEditingSectionId(section.sectionId); setNewSection(null); }}
                        className="text-blue-600 hover:text-blue-800 p-1.5 rounded hover:bg-blue-50"
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteSection(section.sectionId)}
                        className="text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50"
                      >
                        <IoTrash size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Images */}
                  {section.photoIDs?.length > 0 && (
                    <div className="mt-2">
                      {section.photoIDs.length <= 1 ? (
                        <div className="flex flex-wrap gap-2">
                          {section.photoIDs.map((id, i) => (
                            <Image
                              key={i}
                              width={90}
                              height={90}
                              src={getImg(id)}
                              alt={`img ${i + 1}`}
                              className="object-cover rounded border"
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 items-start">
                          {(seeMore ? section.photoIDs : [section.photoIDs[0]]).map((id, i) => (
                            <Image
                              key={i}
                              width={90}
                              height={90}
                              src={getImg(id)}
                              alt={`img ${i + 1}`}
                              className="object-cover rounded border"
                            />
                          ))}
                          <button
                            onClick={() => setSeeMore((p) => !p)}
                            className="text-blue-600 hover:text-blue-800 text-xs transition-colors"
                          >
                            {seeMore ? "See Less" : `See More (${section.photoIDs.length - 1} more)`}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: PhysioTherapy Assessment Panel
// ─────────────────────────────────────────────────────────────────────────────

export function PhysioAssessmentPanel({
  assessmentForm, assessmentApiState, setAssessmentApiState,
  onSave, fetchReport, physioTherapyData, patientInfo,
  reportCount, currentReportIndex, onReportIndexChange,session
}) {
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    assessmentForm.setFieldsValue({
      ptName:              patientInfo?.firstName +" " + patientInfo.lastName || "",
      ptAge:               calculateAge(patientInfo?.birthDay)  || "",
      ptGender:            patientInfo?.gender,
      ptPhone:             patientInfo?.phoneNumber|| "",
      ptDate:              formatDateTime(session?.sessionDate || ""),
      ptDiagnosis:           physioTherapyData?.diagnosis,
      ptPastHistory:         physioTherapyData?.pastHistory,
      ptChiefComplain:       physioTherapyData?.chiefComplain,
      ptJob:                 physioTherapyData?.jobAndOccupation,
      ptPainScore:           physioTherapyData?.painScore || "",
      ptPainDistribution:    physioTherapyData?.painDistribution,
      ptLymphaticEdema:      physioTherapyData?.lymphaticEdema ? ["yes"] : ["no"],
      ptRadioInvestigations: physioTherapyData?.radioInvestigations,
      ptLabNotes:            physioTherapyData?.labNotes ? ["yes"] : ["no"],
      ptLabResults:          physioTherapyData?.labResults,
      ptPrecautions:         physioTherapyData?.precautions,
      ptAssessment:          physioTherapyData?.ptAssessment,
      ptPlanOfTreatment:     physioTherapyData?.ptPlanOfTreatment,
      ptSessionsNum:         physioTherapyData?.sessionsNum ? [String(physioTherapyData.sessionsNum)] : [],
      ptProgressNotes:       physioTherapyData?.progressNotes,
      ptTttGoals:            physioTherapyData?.tttGoals,
      ptDischarge:           physioTherapyData?.discharge,
      ptOtherNotes:          physioTherapyData?.otherNotes,
    });
  }, [physioTherapyData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const f = assessmentForm.getFieldsValue();
      const fields = {
        diagnosis:           f.ptDiagnosis                      || "",
        pastHistory:         f.ptPastHistory                    || "",
        chiefComplain:       f.ptChiefComplain                  || "",
        jobAndOccupation:    f.ptJob                            || "",
        painScore:           Number(f.ptPainScore)              || 0,
        painDistribution:    f.ptPainDistribution               || "",
        lymphaticEdema:      f.ptLymphaticEdema?.includes("yes")?? false,
        radioInvestigations: f.ptRadioInvestigations            || "",
        labNotes:            f.ptLabNotes?.includes("yes")      ?? false,
        labResults:          f.ptLabResults                     || "",
        precautions:         f.ptPrecautions                    || "",
        ptAssessment:        f.ptAssessment                     || "",
        ptPlanOfTreatment:   f.ptPlanOfTreatment                || "",
        sessionsNum:         Number(f.ptSessionsNum?.[0])       || 0,
        progressNotes:       f.ptProgressNotes                  || "",
        tttGoals:            f.ptTttGoals                       || "",
        discharge:           f.ptDischarge                      || "",
        otherNotes:          f.ptOtherNotes                     || "",
      };
      setAssessmentApiState((p) => ({ ...p, ...fields }));
      await onSave(fields);
      await fetchReport?.();
      message.success("Assessment saved");
    } catch { message.error("Failed to save assessment"); }
    finally { setSaving(false); }
  };
  const handlePrintAssessment = () => {
  const formValues = assessmentForm.getFieldsValue();
  const html = buildPrintPhisoHTML({ formValues, physioTherapyData });

  const printWindow = window.open("", "_blank", "width=900,height=700");
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  // Wait for image to load before printing
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
};

  return (
    <div className="space-y-4 sm:space-y-6 overflow-auto">
      {/* Top bar */}
<div className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm flex items-center justify-between gap-2 px-4 py-2">
            {reportCount > 1 ? (
          <PaginationControl current={currentReportIndex} total={reportCount} onChange={onReportIndexChange} />
        ) : <div />}

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrintAssessment}
            className="flex items-center gap-1.5 bg-gray-100 text-gray-700 border border-gray-300 px-3 sm:px-5 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold shadow-sm text-sm"
          >
            <FiPrinter size={16} />
            <span className="hidden sm:block">Print</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 bg-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm font-semibold"
          >
            <FiSave size={15} />
            <span className="hidden sm:block">{saving ? "Saving..." : "Save Assessment"}</span>
          </button>
        </div>
      </div>

      <div className="font-serif">
        {/* Title */}
        <div className="text-center mb-4 pb-3 border-b-2 border-gray-400">
          <h2 className="text-base sm:text-lg font-bold underline tracking-wide text-gray-900">
            Physical Therapy Outpatient Assessment
          </h2>
        </div>

        <Form form={assessmentForm} layout="vertical">
          <AssessmentFormFields isMobile={false} />

          {/* Body diagram */}
          <div className="border border-gray-300 rounded-lg p-3 mb-4 bg-gray-50">
            <div className="flex justify-center py-2">
              <Image
                className="w-full max-w-xs sm:max-w-sm md:w-44 object-contain"
                src="/phisoImg.png"
              />
            </div>
          </div>

          {/* Assessment table */}
          <div className="border border-gray-400 mb-4 overflow-x-auto">
            <table className="w-full border-collapse text-sm" style={{ minWidth: 280 }}>
              <tbody>
                {[
                  { name: "ptAssessment",      label: "PT Assessment",        rows: 3, placeholder: "Enter assessment details..." },
                  { name: "ptPlanOfTreatment", label: "PT Plan of Treatment", rows: 3, placeholder: "Enter treatment plan..." },
                ].map(({ name, label, rows, placeholder }) => (
                  <tr key={name} className="border-b border-gray-400">
                    <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50 w-28 sm:w-36 align-top text-xs sm:text-sm whitespace-nowrap">
                      {label}
                    </td>
                    <td className="p-0">
                      <Form.Item name={name} className="mb-0">
                        <TextArea
                          autoSize={{ minRows: rows }}
                          bordered={false}
                          className="rounded-none resize-none text-sm"
                          placeholder={placeholder}
                        />
                      </Form.Item>
                    </td>
                  </tr>
                ))}

                {/* Sessions number row */}
                <tr className="border-b border-gray-400">
                  <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50 align-top text-xs sm:text-sm whitespace-nowrap">
                    Sessions Num:
                  </td>
                  <td className="p-2 sm:p-3">
                    <Form.Item name="ptSessionsNum" className="mb-0">
                      <Checkbox.Group className="flex flex-wrap gap-x-2 gap-y-1.5 sm:gap-3">
                        {["3", "6", "9", "12", "18", "24", "More"].map((v) => (
                          <Checkbox key={v} value={v} className="text-xs sm:text-sm">
                            {v}
                          </Checkbox>
                        ))}
                      </Checkbox.Group>
                    </Form.Item>
                  </td>
                </tr>

                {/* Progress notes */}
                <tr>
                  <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50 align-top text-xs sm:text-sm whitespace-nowrap">
                    Progress Notes
                  </td>
                  <td className="p-0">
                    <Form.Item name="ptProgressNotes" className="mb-0">
                      <TextArea
                        autoSize={{ minRows: 2 }}
                        bordered={false}
                        className="rounded-none resize-none text-sm"
                      />
                    </Form.Item>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Bottom inline fields */}
          <div className="space-y-2">
            {[
              { name: "ptTttGoals",   label: "TTT Goals:"      },
              { name: "ptDischarge",  label: "Discharge:"       },
              { name: "ptOtherNotes", label: "Any Other Notes:" },
            ].map(({ name, label }) => (
              <div
                key={name}
                className="flex items-start sm:items-center gap-2 pb-1 border-b border-dashed border-gray-300"
              >
                <span className="text-xs sm:text-sm font-semibold whitespace-nowrap pt-1 sm:pt-0">
                  {label}
                </span>
                <Form.Item name={name} className="mb-0 flex-1">
                  <Input bordered={false} className="border-b border-gray-400 rounded-none px-0 text-sm" />
                </Form.Item>
              </div>
            ))}
          </div>
        </Form>

        <SignatureFooter />
      </div>
      <div className="mt-5 z-10 bg-white border-b border-gray-100 shadow-sm flex items-center justify-between gap-2 px-4 py-2">
            {reportCount > 1 ? (
          <PaginationControl current={currentReportIndex} total={reportCount} onChange={onReportIndexChange} />
        ) : <div />}

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrintAssessment}
            className="flex items-center gap-1.5 bg-gray-100 text-gray-700 border border-gray-300 px-3 sm:px-5 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold shadow-sm text-sm"
          >
            <FiPrinter size={16} />
            <span className="hidden sm:block">Print</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 bg-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm font-semibold"
          >
            <FiSave size={15} />
            <span className="hidden sm:block">{saving ? "Saving..." : "Save Assessment"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: Lymphedema Assessment Panel
// ─────────────────────────────────────────────────────────────────────────────

export function LymphedemaAssessmentPanel({
  assessmentForm, assessmentApiState, setAssessmentApiState,
  onSave, fetchReport, lymphedemaData, reportId, patientInfo,
  reportCount, currentReportIndex, onReportIndexChange,session
}) {
  const [saving,       setSaving]       = useState(false);
  const [measurementIds,setMeasurementIds]=useState({});
  const [deletingRow,  setDeletingRow]  = useState(null);
  const [savingRow,    setSavingRow]    = useState(null);

  useEffect(() => {
    assessmentForm.setFieldsValue({
      ptName:              patientInfo?.firstName +" "+ patientInfo?.lastName || "",
      ptAge:               calculateAge(patientInfo?.birthDay  || ""),
      ptGender:            patientInfo?.gender,
      ptPhone:             patientInfo?.phoneNumber|| "",
      ptDate:              formatDateTime(session?.sessionDate || ""),
      ptDiagnosis:           lymphedemaData?.diagnosis,
      ptPastHistory:         lymphedemaData?.pastHistory,
      ptChiefComplain:       lymphedemaData?.chiefComplain,
      ptJob:                 lymphedemaData?.jobAndOccupation,
      ptPainScore:           lymphedemaData?.painScore || "",
      ptPainDistribution:    lymphedemaData?.painDistribution,
      ptLymphaticEdema:      lymphedemaData?.lymphaticEdema ? ["yes"] : ["no"],
      ptRadioInvestigations: lymphedemaData?.radioInvestigations,
      ptLabNotes:            lymphedemaData?.labNotes ? ["yes"] : ["no"],
      ptLabResults:          lymphedemaData?.labResults,
      ptAssessment:          lymphedemaData?.ptAssessment,
      ptPlanOfTreatment:     lymphedemaData?.ptPlanOfTreatment,
      sessionsNum:           lymphedemaData?.sessionsNum ? [String(lymphedemaData.sessionsNum)] : [],
      ptProgressNotes:       lymphedemaData?.progressNotes,
      ptTttGoals:            lymphedemaData?.tttGoals,
      ptDischarge:           lymphedemaData?.discharge,
      ptOtherNotes:          lymphedemaData?.otherNotes,
      ptOperations:          lymphedemaData?.operations        || "",
      ptPresentHistory:      lymphedemaData?.presentHistory    || "",
      ptSkinAbnormalities:   lymphedemaData?.skinAbnormalities || "",
      ptPrecautions:         lymphedemaData?.precautions       || "",
      ptTreatmentOptions: [
        lymphedemaData?.treatmentMLD       && "MLD",
        lymphedemaData?.treatmentPCD       && "PCD",
        lymphedemaData?.treatmentTECAR     && "TECAR",
        lymphedemaData?.treatmentBandaging && "Bandaging",
        lymphedemaData?.treatmentExercises && "Exercises",
      ].filter(Boolean),
      ptTreatmentOther: lymphedemaData?.treatmentOther || "",
    });

    const ids = {};
    (lymphedemaData?.measurements || []).forEach((m, idx) => { if (m.id) ids[idx] = m.id; });
    setMeasurementIds(ids);

    const measFields = {};
    [1, 2, 3, 4].forEach((col) => {
      const first = (lymphedemaData?.measurements || []).find((m) => m[`session${col}Date`]);
      if (first) measFields[`session${col}Date`] = dayjs(first[`session${col}Date`]);
    });
    (lymphedemaData?.measurements || []).forEach((m, idx) => {
      measFields[`ptMeasPoint_${idx}`] = m.pointOfMeasurement ?? "";
      [1, 2, 3, 4].forEach((col) => {
        measFields[`ptMeas_${idx}_${col}_RT`] = m[`session${col}RT`] ?? "";
        measFields[`ptMeas_${idx}_${col}_LT`] = m[`session${col}LT`] ?? "";
      });
    });
    assessmentForm.setFieldsValue(measFields);
  }, [lymphedemaData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const f = assessmentForm.getFieldsValue();
      const treatmentOpts = f.ptTreatmentOptions || [];
      const updatedIds = { ...measurementIds };

      await Promise.all(ROW_INDICES.map(async (idx) => {
        const point = (f[`ptMeasPoint_${idx}`] ?? "").trim();
        if (!point) return;
        const existingId = updatedIds[idx];
        if (existingId) {
          await updateMeasurement(existingId, point, idx, f);
        } else {
          const result = await addMeasurement(reportId, point, idx, f);
          if (result?.id) updatedIds[idx] = result.id;
        }
      }));

      setMeasurementIds(updatedIds);

      const fields = {
        diagnosis:           f.ptDiagnosis                       || "",
        pastHistory:         f.ptPastHistory                     || "",
        chiefComplain:       f.ptChiefComplain                   || "",
        jobAndOccupation:    f.ptJob                             || "",
        painScore:           Number(f.ptPainScore)               || 0,
        painDistribution:    f.ptPainDistribution                || "",
        lymphaticEdema:      f.ptLymphaticEdema?.includes("yes") ?? false,
        radioInvestigations: f.ptRadioInvestigations             || "",
        labNotes:            f.ptLabNotes?.includes("yes")       ?? false,
        labResults:          f.ptLabResults                      || "",
        precautions:         f.ptPrecautions                     || "",
        ptAssessment:        f.ptAssessment                      || "",
        ptPlanOfTreatment:   f.ptPlanOfTreatment                 || "",
        sessionsNum:         Number(f.sessionsNum?.[0])          || 0,
        progressNotes:       f.ptProgressNotes                   || "",
        tttGoals:            f.ptTttGoals                        || "",
        discharge:           f.ptDischarge                       || "",
        otherNotes:          f.ptOtherNotes                      || "",
        operations:          f.ptOperations                      || "",
        presentHistory:      f.ptPresentHistory                  || "",
        skinAbnormalities:   f.ptSkinAbnormalities               || "",
        treatmentMLD:        treatmentOpts.includes("MLD"),
        treatmentPCD:        treatmentOpts.includes("PCD"),
        treatmentTECAR:      treatmentOpts.includes("TECAR"),
        treatmentBandaging:  treatmentOpts.includes("Bandaging"),
        treatmentExercises:  treatmentOpts.includes("Exercises"),
        treatmentOther:      f.ptTreatmentOther                  || "",
      };
      setAssessmentApiState((p) => ({ ...p, ...fields }));
      await onSave(fields);
      await fetchReport?.();
      message.success("Assessment saved");
    } catch { message.error("Failed to save assessment"); }
    finally { setSaving(false); }
  };

  const handleSaveRow = async (idx) => {
    const f = assessmentForm.getFieldsValue();
    const point = (f[`ptMeasPoint_${idx}`] ?? "").trim();
    if (!point) return message.warning("Please enter a point of measurement before saving.");
    setSavingRow(idx);
    try {
      const existingId = measurementIds[idx];
      if (existingId) { await updateMeasurement(existingId, point, idx, f); message.success("Measurement updated"); }
      else { await addMeasurement(reportId, point, idx, f); message.success("Measurement saved"); }
      await fetchReport?.();
    } catch { message.error("Failed to save measurement"); }
    finally { setSavingRow(null); }
  };

  const handleDeleteRow = async (idx) => {
    setDeletingRow(idx);
    try {
      if (measurementIds[idx]) await deleteMeasurementApi(measurementIds[idx]);
      const cleared = { [`ptMeasPoint_${idx}`]: "" };
      [1, 2, 3, 4].forEach((col) => {
        cleared[`ptMeas_${idx}_${col}_RT`] = "";
        cleared[`ptMeas_${idx}_${col}_LT`] = "";
      });
      assessmentForm.setFieldsValue(cleared);
      setMeasurementIds((p) => { const n = { ...p }; delete n[idx]; return n; });
      await fetchReport?.();
      message.success("Row deleted");
    } catch { message.error("Failed to delete measurement"); }
    finally { setDeletingRow(null); }
  };
    const handlePrintAssessment = () => {
        const f = assessmentForm.getFieldsValue();
        const html = buildPrintLymphedemaHTML({ f, lymphedemaData });

        const printWindow = window.open("", "_blank", "width=900,height=700");
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Lymphedema Assessment Sheet</title>
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    body {
                        font-family: Georgia, 'Times New Roman', serif;
                        font-size: 11px;
                        color: #000;
                        background: #fff;
                        padding: 20px 28px;
                    }
                    h2 {
                        text-align: center;
                        font-size: 15px;
                        font-weight: bold;
                        text-decoration: underline;
                        letter-spacing: 0.05em;
                        margin-bottom: 10px;
                        padding-bottom: 6px;
                        border-bottom: 2px solid #555;
                    }
                    .field-row {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 6px 16px;
                        align-items: flex-end;
                        padding: 5px 0;
                        border-bottom: 1px dashed #bbb;
                        margin-bottom: 4px;
                    }
                    .field-group {
                        display: flex;
                        align-items: flex-end;
                        gap: 4px;
                        flex: 1;
                        min-width: 100px;
                    }
                    .field-label { font-weight: bold; white-space: nowrap; font-size: 11px; }
                    .field-value {
                        border-bottom: 1px solid #555;
                        flex: 1;
                        min-width: 50px;
                        padding: 1px 2px;
                        font-size: 11px;
                        min-height: 16px;
                    }
                    .field-value-multiline {
                        border-bottom: 1px solid #555;
                        flex: 1;
                        min-width: 80px;
                        padding: 2px 3px;
                        min-height: 32px;
                        font-size: 11px;
                        white-space: pre-wrap;
                    }
                    .check-group { display: flex; gap: 8px; align-items: center; }
                    .check-item { display: flex; align-items: center; gap: 3px; font-size: 11px; }
                    .check-box {
                        width: 11px; height: 11px;
                        border: 1.5px solid #444;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 9px;
                        flex-shrink: 0;
                    }
                    .check-box.checked::after { content: "✓"; font-weight: bold; }
                    .body-section {
                        display: flex;
                        gap: 14px;
                        padding: 6px 0;
                        border-bottom: 1px dashed #bbb;
                        margin-bottom: 5px;
                    }
                    .body-image { width: 130px; flex-shrink: 0; }
                    .body-image img { width: 100%; height: auto; }
                    .body-fields { flex: 1; display: flex; flex-direction: column; gap: 7px; }
                    table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 6px; }
                    th, td { border: 1px solid #666; padding: 3px 4px; text-align: center; }
                    th { background: #e8e8e8; font-weight: bold; }
                    td.point-cell { text-align: left; }
                    .signatures {
                        display: flex;
                        justify-content: space-between;
                        margin-top: 30px;
                        padding-top: 10px;
                        border-top: 1px solid #aaa;
                    }
                    .sig-block { text-align: center; }
                    .sig-line { width: 160px; border-bottom: 1px solid #555; height: 30px; margin-bottom: 4px; }
                    @media print {
                        body { padding: 8px 14px; }
                        @page { margin: 8mm; size: A4; }
                    }
                </style>
            </head>
            <body>${html}</body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
    };

return (
    <div className="space-y-4 sm:space-y-6">
      {/* Top bar */}
      <div className="flex items-center justify-between  bg-white  gap-2">
        {reportCount > 1 ? (
          <PaginationControl current={currentReportIndex} total={reportCount} onChange={onReportIndexChange} />
        ) : <div />}

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrintAssessment}
            className="flex items-center gap-1.5 bg-gray-100 text-gray-700 border border-gray-300 px-3 sm:px-5 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold shadow-sm text-sm"
          >
            <FiPrinter size={16} />
            <span className="hidden sm:block">Print</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 bg-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm font-semibold"
          >
            <FiSave size={15} />
            <span className="hidden sm:block">{saving ? "Saving..." : "Save Assessment"}</span>
          </button>
        </div>
      </div>

      <div className="font-serif">
        {/* Title */}
        <div className="text-center mb-4 pb-3 border-b-2 border-gray-400">
          <h2 className="text-base sm:text-lg font-bold underline tracking-wide text-gray-900">
            Lymphedema Assessment Sheet
          </h2>
        </div>

        <Form form={assessmentForm} layout="vertical">
          <AssessmentFormFields isMobile={false} />

          {/* Body image + side fields — stack on mobile */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pb-3 mb-3 border-b border-dashed border-gray-300">
            {/* Image — centered & smaller on mobile */}
            <div className="w-full sm:w-20 md:w-48 sm:flex-shrink-0 flex justify-center">
              <img
                src="/lymphoImg.png"
                className="w-32 sm:w-full object-contain"
              />
            </div>

            {/* Form fields */}
            <div className="flex-1 space-y-3">
              <Form.Item
                name="ptOperations"
                label={<span className="font-semibold text-sm">Operations:</span>}
                className="mb-0"
              >
                <TextArea
                  autoSize={{ minRows: 2 }}
                  className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent"
                />
              </Form.Item>

              <Form.Item
                name="ptPresentHistory"
                label={<span className="font-semibold text-sm">Present History:</span>}
                className="mb-0"
              >
                <TextArea
                  autoSize={{ minRows: 2 }}
                  className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent"
                />
              </Form.Item>

              <Form.Item
                name="ptSkinAbnormalities"
                label={<span className="font-semibold text-sm">Skin Abnormalities:</span>}
                className="mb-0"
              >
                <TextArea
                  autoSize={{ minRows: 2 }}
                  className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent"
                />
              </Form.Item>

              <div>
                <span className="font-semibold text-sm block mb-1">Plan of Treatment:</span>
                <Form.Item name="ptTreatmentOptions" className="mb-1">
                  <Checkbox.Group className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {["MLD", "PCD", "TECAR", "Bandaging", "Exercises"].map((v) => (
                      <Checkbox key={v} value={v}>
                        {v === "Exercises" ? "Ex's" : v}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold whitespace-nowrap">Other:</span>
                  <Form.Item name="ptTreatmentOther" className="mb-0 flex-1">
                    <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                  </Form.Item>
                </div>
              </div>

              <Form.Item
                name="ptPrecautions"
                label={<span className="font-semibold text-sm">Precautions:</span>}
                className="mb-0"
              >
                <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
              </Form.Item>
            </div>
          </div>

          {/* Measurement table — horizontal scroll on mobile */}
          <div className="overflow-x-auto -mx-1 px-1 pb-3 mb-3 border-b border-dashed border-gray-300">
            <table
              className="border-collapse text-sm text-center"
              style={{ minWidth: 600, width: "100%" }}
            >
              <thead>
                {/* Sessions number row */}
                <tr>
                  <th
                    className="border border-gray-400 bg-gray-100 font-bold px-2 py-1 text-left"
                    colSpan={11}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="whitespace-nowrap text-xs sm:text-sm">Sessions Num:</span>
                      <Form.Item name="sessionsNum" className="mb-0">
                        <Checkbox.Group className="flex flex-wrap gap-x-2 sm:gap-x-3">
                          {["3", "6", "9", "12", "18", "24", "More"].map((n) => (
                            <Checkbox key={n} value={n} className="text-xs sm:text-sm">
                              {n}
                            </Checkbox>
                          ))}
                        </Checkbox.Group>
                      </Form.Item>
                    </div>
                  </th>
                </tr>

                {/* Date row */}
                <tr>
                  <th className="border border-gray-400 bg-gray-100 font-bold px-2 py-1 text-xs">
                    Date:
                  </th>
                  {[1, 2, 3, 4].map((col) => (
                    <th key={col} colSpan={2} className="border border-gray-400 bg-gray-50 px-1 py-0.5">
                      <Form.Item name={`session${col}Date`} className="mb-0">
                        <DatePicker
                          format="DD/MM/YYYY"
                          size="small"
                          className="w-full border-0 border-b border-gray-500 rounded-none bg-transparent shadow-none text-xs"
                          placeholder="DD/MM/YYYY"
                        />
                      </Form.Item>
                    </th>
                  ))}
                  <th className="border border-gray-400 bg-gray-100 w-12" />
                </tr>

                {/* RT / LT header row */}
                <tr>
                  <th className="border border-gray-400 bg-gray-100 font-bold px-2 py-1 text-xs">
                    Point of Measurement
                  </th>
                  {[1, 2, 3, 4].map((col) => (
                    <React.Fragment key={col}>
                      <th className="border border-gray-400 bg-gray-100 font-semibold px-1 py-1 text-xs">
                        RT
                      </th>
                      <th className="border border-gray-400 bg-gray-100 font-semibold px-1 py-1 text-xs">
                        LT
                      </th>
                    </React.Fragment>
                  ))}
                  <th className="border border-gray-400 bg-gray-100 font-semibold px-1 py-1 text-xs w-12">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {ROW_INDICES.map((idx) => (
                  <tr key={idx}>
                    <td className="border border-gray-400 px-1 py-0.5">
                      <Form.Item name={`ptMeasPoint_${idx}`} className="mb-0">
                        <Input
                          placeholder="Enter point..."
                          className="border-0 rounded-none px-1 bg-transparent text-xs placeholder:text-gray-300"
                        />
                      </Form.Item>
                    </td>
                    {[1, 2, 3, 4].map((col) => (
                      <React.Fragment key={col}>
                        <td className="border border-gray-400 px-1 py-0.5">
                          <Form.Item name={`ptMeas_${idx}_${col}_RT`} className="mb-0">
                            <Input className="border-0 rounded-none px-0 text-center bg-transparent text-xs" />
                          </Form.Item>
                        </td>
                        <td className="border border-gray-400 px-1 py-0.5">
                          <Form.Item name={`ptMeas_${idx}_${col}_LT`} className="mb-0">
                            <Input className="border-0 rounded-none px-0 text-center bg-transparent text-xs" />
                          </Form.Item>
                        </td>
                      </React.Fragment>
                    ))}
                    <td className="border border-gray-400 px-1 py-0.5 text-center w-12">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleSaveRow(idx)}
                          disabled={savingRow === idx || deletingRow === idx}
                          className="text-blue-400 hover:text-blue-600 disabled:opacity-40 p-0.5"
                        >
                          <FiSave size={13} />
                        </button>
                        {measurementIds[idx] && (
                          <Popconfirm
                            title="Delete this measurement?"
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDeleteRow(idx)}
                          >
                            <button
                              type="button"
                              disabled={deletingRow === idx || savingRow === idx}
                              className="text-red-400 hover:text-red-600 disabled:opacity-40 p-0.5"
                            >
                              <FiTrash2 size={13} />
                            </button>
                          </Popconfirm>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Form>

        <SignatureFooter />
      </div>
       <div className="mt-5 flex items-center justify-between  bg-white  gap-2">
        {reportCount > 1 ? (
          <PaginationControl current={currentReportIndex} total={reportCount} onChange={onReportIndexChange} />
        ) : <div />}

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrintAssessment}
            className="flex items-center gap-1.5 bg-gray-100 text-gray-700 border border-gray-300 px-3 sm:px-5 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold shadow-sm text-sm"
          >
            <FiPrinter size={16} />
            <span className="hidden sm:block">Print</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 bg-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm font-semibold"
          >
            <FiSave size={15} />
            <span className="hidden sm:block">{saving ? "Saving..." : "Save Assessment"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
